import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Text from "components/Input/Text";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { deleteOrgService, readService, updateOrgService } from "services/org-services.service";
import vgToast from "utils/toast";

type FormValues = {
  name: string;
  description: string;
};

const UpdateServiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently } = useAuth0();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  // Save stays off until the service has been read: submitting before then
  // would overwrite it with the snapshot's zero price and tax.
  const [loaded, setLoaded] = React.useState(false);

  const serviceId = id ? Number(id) : NaN;
  const snapshotRef = React.useRef<{
    taxRate: number;
    monetaryAmount: number;
    monetaryCurrency: number;
  }>({ taxRate: 0, monetaryAmount: 0, monetaryCurrency: 978 });

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<FormValues>();

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoaded(false);
      if (!id || !Number.isFinite(serviceId) || serviceId <= 0 || orgId <= 0) return;
      setLoadError(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await readService(token, orgId, serviceId);
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        if (!data) return;
        snapshotRef.current = {
          taxRate: data.taxRate ?? 0,
          monetaryAmount: data.monetaryAmount ?? 0,
          monetaryCurrency: data.monetaryCurrency ?? 978,
        };
        reset({
          name: data.name,
          description: data.description,
        });
        setLoaded(true);
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, serviceId, orgId, getAccessTokenSilently, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0 || !Number.isFinite(serviceId) || serviceId <= 0) {
      vgToast.error("Missing organisation or service id.");
      return;
    }
    if (!loaded) return;
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const snap = snapshotRef.current;
      const { error } = await updateOrgService(token, {
        orgId,
        serviceId,
        name: data.name,
        description: data.description,
        taxRate: snap.taxRate,
        monetaryAmount: snap.monetaryAmount,
        monetaryCurrency: snap.monetaryCurrency,
      });
      if (error) vgToast.error(error.message);
      else vgToast.success("Service saved.");
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this service permanently?")) return;
    if (orgId <= 0 || !Number.isFinite(serviceId) || serviceId <= 0) return;
    setDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error } = await deleteOrgService(token, orgId, serviceId);
      if (error) {
        vgToast.error(error.message);
        return;
      }
      navigate("/app/services");
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-10">
      {loadError && (
        <div className="mb-4 text-sm text-red-500">{loadError}</div>
      )}
      <div className="flex flex-col gap-y-10 mb-10">
        <Input
          {...register("name")}
          label="Service Name"
          placeholder="Enter Service Name"
        />

        <Text
          {...register("description")}
          label="Service Description"
          placeholder="Enter Service Description"
        />
      </div>

      <div className="flex justify-between gap-4 flex-wrap">
        <Button
          color="Secondary"
          type="button"
          disabled={deleting || submitting}
          onClick={handleDelete}
        >
          <span>{deleting ? "Deleting…" : "Delete service"}</span>
        </Button>
        <Button color="Primary" type="submit" disabled={!loaded || submitting || deleting}>
          <span>{submitting ? "Saving…" : "Update"}</span>
        </Button>
      </div>
    </form>
  );
};

export default UpdateServiceForm;
